import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import Modal from '../components/Modal';
import {
  Badge,
  Button,
  FormGrid,
  FormRow,
  Input,
  Label,
  Muted,
  Page,
  PageActions,
  PageHeader,
  PageHeaderMain,
  Select,
  Subtitle,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  Table,
  TableWrap,
  TextArea,
  Title,
  Toast,
  Toolbar,
} from '../components/ui';

interface Auction {
  _id: string;
  title: string;
  description: string;
  imageUrl: string;
  startingPrice: number;
  currentHighBid: number;
  status: string;
  minFollowMonths: number;
  announceBidsInChat?: boolean;
}

const emptyForm = {
  title: '',
  description: '',
  startingPrice: '5000',
  minimumBid: '500',
  status: 'draft',
  minFollowMonths: '3',
  imageUrl: '',
  announceBidsInChat: false,
};

const statusLabel: Record<string, string> = {
  draft: 'Oculta',
  active: 'Activa',
  paused: 'Pausada',
  closed: 'Cerrada',
};

const AuctionsPage: React.FC = () => {
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [publicEnabled, setPublicEnabled] = useState(true);
  const [savingPublic, setSavingPublic] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [toast, setToast] = useState('');

  const load = async () => {
    const [{ data: auctionList }, { data: publicCfg }] = await Promise.all([
      api.get('/auctions/admin/all'),
      api.get('/config/auctions-public'),
    ]);
    setAuctions(auctionList);
    setPublicEnabled(publicCfg.auctionsPublicEnabled !== false);
  };

  useEffect(() => { load(); }, []);

  const closeModal = () => {
    setModalOpen(false);
    setForm(emptyForm);
    setImageFile(null);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('title', form.title);
    fd.append('description', form.description);
    fd.append('startingPrice', form.startingPrice);
    fd.append('minimumBid', form.minimumBid);
    fd.append('status', form.status);
    fd.append('minFollowMonths', form.minFollowMonths);
    fd.append('announceBidsInChat', form.announceBidsInChat ? 'true' : 'false');
    if (form.imageUrl) fd.append('imageUrl', form.imageUrl);
    if (imageFile) fd.append('image', imageFile);

    await api.post('/auctions', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    setToast('Subasta creada');
    closeModal();
    load();
  };

  const togglePublicSite = async () => {
    setSavingPublic(true);
    try {
      const { data } = await api.put('/config/auctions-public', { enabled: !publicEnabled });
      setPublicEnabled(data.auctionsPublicEnabled);
      setToast(data.auctionsPublicEnabled ? 'Subastas visibles en el sitio' : 'Subastas ocultas en el sitio');
    } finally {
      setSavingPublic(false);
    }
  };

  return (
    <Page>
      <PageHeader>
        <PageHeaderMain>
          <Title>Subastas</Title>
          <Subtitle>Gestioná obras, estados y visibilidad en eugenial.com.ar.</Subtitle>
        </PageHeaderMain>
        <PageActions>
          <Button type="button" onClick={() => setModalOpen(true)}>+ Nueva subasta</Button>
        </PageActions>
      </PageHeader>

      {toast && <Toast>{toast}</Toast>}

      <Surface>
        <SurfaceHeader>
          <Toolbar style={{ flex: 1, justifyContent: 'space-between' }}>
            <div>
              <Muted style={{ margin: 0, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Visibilidad en eugenial.com.ar
              </Muted>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', marginTop: '0.35rem', color: '#e8dff0', fontSize: '0.86rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={publicEnabled}
                  disabled={savingPublic}
                  onChange={togglePublicSite}
                />
                Mostrar sección Subastas en el sitio público
              </label>
            </div>
            <Badge $active={publicEnabled}>{publicEnabled ? 'Visible' : 'Oculta'}</Badge>
          </Toolbar>
        </SurfaceHeader>
        <SurfaceBody $flush>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Obra</th>
                  <th style={{ width: '11%' }}>Estado</th>
                  <th style={{ width: '8%' }}>Chat</th>
                  <th style={{ width: '10%' }}>Base</th>
                  <th style={{ width: '10%' }}>Actual</th>
                  <th style={{ width: '12%', textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {auctions.map((a) => (
                  <tr key={a._id}>
                    <td>{a.title}</td>
                    <td><Badge $active={a.status === 'active'}>{statusLabel[a.status] || a.status}</Badge></td>
                    <td>{a.announceBidsInChat ? 'Sí' : 'No'}</td>
                    <td>${a.startingPrice}</td>
                    <td>${a.currentHighBid || '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Link to={`/auctions/${a._id}`}>
                        <Button $variant="ghost" $size="sm" type="button">Gestionar</Button>
                      </Link>
                    </td>
                  </tr>
                ))}
                {!auctions.length && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '1.5rem' }}>Sin subastas</td></tr>
                )}
              </tbody>
            </Table>
          </TableWrap>
        </SurfaceBody>
      </Surface>

      <Modal
        open={modalOpen}
        title="Nueva subasta"
        onClose={closeModal}
        wide
        footer={(
          <>
            <Button $variant="ghost" type="button" onClick={closeModal}>Cancelar</Button>
            <Button type="submit" form="auction-create-form">Crear subasta</Button>
          </>
        )}
      >
        <form id="auction-create-form" onSubmit={save}>
          <FormGrid>
            <FormRow style={{ gridColumn: '1 / -1' }}>
              <Label>Título</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            </FormRow>
            <FormRow style={{ gridColumn: '1 / -1' }}>
              <Label>Descripción</Label>
              <TextArea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} />
            </FormRow>
            <FormRow>
              <Label>Precio base (ARS)</Label>
              <Input type="number" value={form.startingPrice} onChange={(e) => setForm({ ...form, startingPrice: e.target.value })} />
            </FormRow>
            <FormRow>
              <Label>Puja mínima (ARS)</Label>
              <Input type="number" min={1} value={form.minimumBid} onChange={(e) => setForm({ ...form, minimumBid: e.target.value })} />
            </FormRow>
            <FormRow>
              <Label>Estado</Label>
              <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="draft">Oculta</option>
                <option value="active">Activa</option>
                <option value="paused">Pausada</option>
                <option value="closed">Cerrada</option>
              </Select>
            </FormRow>
            <FormRow>
              <Label>Mín. meses de follow</Label>
              <Input type="number" value={form.minFollowMonths} onChange={(e) => setForm({ ...form, minFollowMonths: e.target.value })} />
            </FormRow>
            <FormRow style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', color: '#b8a9c9', fontSize: '0.84rem' }}>
                <input
                  type="checkbox"
                  checked={form.announceBidsInChat}
                  onChange={(e) => setForm({ ...form, announceBidsInChat: e.target.checked })}
                />
                Anunciar pujas en el chat de Twitch (bot)
              </label>
            </FormRow>
            <FormRow>
              <Label>Imagen (archivo)</Label>
              <Input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
            </FormRow>
            <FormRow>
              <Label>O URL imagen</Label>
              <Input value={form.imageUrl} placeholder="/uploads/auctions/batman1.jpeg" onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} />
            </FormRow>
          </FormGrid>
          <Muted style={{ marginTop: '0.5rem' }}>
            Sin pujas previas: al menos el precio base. Con pujas: mínimo = puja actual + puja mínima.
          </Muted>
        </form>
      </Modal>
    </Page>
  );
};

export default AuctionsPage;
